import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { get, post, configureTokenSource } from "~/lib/api";
import type { WireMe } from "./me";
import { currentAccessToken, forgetSession, supabase } from "./supabase";

/**
 * Who is signed in, for the whole app (R22 T10).
 *
 * **One `/me` call, held in one place.** Every screen needs the answer and none
 * of them may ask separately: a dozen components each fetching their own copy
 * would be a dozen chances to render a stale role, and the chrome would flicker
 * between them. It is fetched once at start-up, re-fetched when supabase-js says
 * the session changed, and re-fetched on demand after a command that can change
 * it (an active-clinic switch, a password change).
 *
 * **`loading` is a real state the screens must render.** This is the one thing
 * a client-rendered app has that the server-rendered one did not: on the old
 * host the caller was resolved before a byte was sent, so no screen ever
 * existed without an answer. Here the first paint happens before `/me` returns,
 * and a guard that treats "not yet known" as "guest" would bounce a signed-in
 * member to a log-in screen on every cold load. The screen docs gained a
 * loading state for exactly this reason.
 */

export interface MeContextValue {
  /** Null until the first answer arrives. Never assume guest while null. */
  readonly me: WireMe | null;
  readonly loading: boolean;
  /** Re-read `/me`, for after a command that changes who the caller is. */
  readonly reload: () => Promise<void>;
  /**
   * The API could not be reached, so who is calling is genuinely unknown.
   *
   * Distinct from `loading` and from guest, and it has to be: treating an
   * unreachable API as "guest" would sign the whole app out during a blip,
   * and leaving it as "still loading" would spin a skeleton forever. It is a
   * screen with a retry on it, which is what an outage actually looks like.
   */
  readonly unreachable: boolean;
  /** Forget the session here and on the API, then land on the given path. */
  readonly signOut: () => Promise<void>;
}

const MeContext = createContext<MeContextValue | null>(null);

export function MeProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<WireMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [unreachable, setUnreachable] = useState(false);
  // Guards against a slow first answer overwriting a fresh one, which is the
  // classic way a just-signed-in user briefly reappears as a guest.
  const generation = useRef(0);

  const load = useCallback(async () => {
    const mine = ++generation.current;
    const result = await get<WireMe>("/me");
    if (mine !== generation.current) return;

    // A failed `/me` is not "guest": a network blip would silently sign
    // everybody out. Guest is only ever what the server actually said.
    if (result.ok) {
      setMe(result.value);
      setUnreachable(false);
    } else if (result.failure.code === "session_expired") {
      // The token is stale or forged. That IS an answer: nobody is signed in.
      setMe({ kind: "guest" });
      setUnreachable(false);
    } else {
      setUnreachable(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // The API client asks supabase-js for a token per call, so the two stay in
    // step through a refresh without anything being copied between them.
    configureTokenSource(currentAccessToken);

    // **The first load comes from the subscription, not from a call here.**
    // supabase-js emits INITIAL_SESSION on subscribe, with or without a stored
    // session, so this one subscription covers start-up and every later change.
    // Fetching separately would have raced it: two `/me` calls in flight, and
    // the slower one wins whatever it says.
    const { data } = supabase.auth.onAuthStateChange((event) => {
      // TOKEN_REFRESHED changes the token, not the caller, and re-fetching on
      // it would put a request on the wire every hour for no new information.
      if (event === "TOKEN_REFRESHED") return;
      void load();
    });
    return () => data.subscription.unsubscribe();
  }, [load]);

  const signOut = useCallback(async () => {
    // The API first, while the token is still valid, then the local session.
    // The other order would send an unauthenticated logout, which does nothing.
    await post("/auth/logout");
    await forgetSession();
    setMe({ kind: "guest" });
  }, []);

  const retry = useCallback(async () => {
    setLoading(true);
    await load();
  }, [load]);

  const value = useMemo<MeContextValue>(
    () => ({ me, loading, unreachable, reload: retry, signOut }),
    [me, loading, unreachable, retry, signOut],
  );

  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}

export function useMe(): MeContextValue {
  const value = useContext(MeContext);
  if (!value) throw new Error("useMe must be used inside <MeProvider>");
  return value;
}
